(() => {
  'use strict';
  const libraries = new Map();
  const readLibrary = (url) => {
    if (!libraries.has(url)) {
      libraries.set(url, fetch(url, { credentials: 'omit' }).then((response) => {
        if (!response.ok) throw new Error('Review library unavailable');
        return response.json();
      }).catch((error) => { libraries.delete(url); throw error; }));
    }
    return libraries.get(url);
  };
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const dateLabel = (date) => new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  const normalize = (text) => text.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const compact = root.dataset.compact === 'true';
    const list = root.querySelector('[data-review-list]');
    const more = root.querySelector('[data-review-more]');
    const controls = root.querySelector('[data-review-controls]');
    const search = root.querySelector('[data-review-search]');
    const category = root.querySelector('[data-review-category]');
    const rating = root.querySelector('[data-review-rating]');
    const sort = root.querySelector('[data-review-sort]');
    const photos = root.querySelector('[data-review-photos]');
    const dialog = root.querySelector('[data-review-dialog]');
    let reviews = [], filtered = [], visible = 12, opener = null;
    function photoButton(review, url) {
      const button = el('button', 'review-photo-button');
      button.type = 'button';
      button.setAttribute('aria-label', `Open customer photo and review by ${review.name}`);
      const img = el('img');
      img.src = url;
      img.alt = `Photo shared with ${review.name}'s review`;
      img.width = 360; img.height = 360; img.loading = 'lazy'; img.decoding = 'async';
      img.addEventListener('error', () => { button.hidden = true; });
      button.append(img, el('span', '', 'View photo'));
      button.addEventListener('click', () => {
        opener = button;
        const large = dialog.querySelector('[data-review-large-image]');
        large.src = url; large.alt = img.alt;
        const caption = dialog.querySelector('[data-review-caption]');
        caption.replaceChildren(el('strong', '', `${review.name} · ${review.rating}/5`), el('p', '', review.body || 'Rating without a written review.'), el('p', 'review-card__context', `${review.category} · ${dateLabel(review.date)} · ${review.source}`));
        dialog.showModal();
      });
      return button;
    }
    function card(review) {
      const article = el('article', 'review-card');
      const stars = el('span', 'review-stars', '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating));
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      article.append(stars);
      if (review.photos.length) article.append(photoButton(review, review.photos[0]));
      if (review.body.length > 330) {
        article.append(el('blockquote', '', review.body.slice(0, 220).replace(/\s+\S*$/, '') + '…'));
        const details = el('details');
        details.append(el('summary', '', 'Read full review'), el('p', '', review.body));
        article.append(details);
      } else {
        article.append(el('blockquote', '', review.body || 'Rating without a written review.'));
      }
      const byline = el('div', 'review-card__byline');
      const time = el('time', '', dateLabel(review.date));
      time.dateTime = review.date;
      byline.append(el('strong', '', review.name), time);
      article.append(byline, el('p', 'review-card__context', `${review.category} · ${review.source}`));
      return article;
    }
    function render(append = false) {
      const start = append ? list.children.length : 0;
      if (!append) list.replaceChildren();
      const fragment = document.createDocumentFragment();
      filtered.slice(start, visible).forEach((review) => fragment.append(card(review)));
      list.append(fragment);
      if (!filtered.length) list.append(el('p', '', 'No reviews match these filters. Try another search or choose all ratings.'));
      more.hidden = filtered.length <= visible;
      root.querySelector('[data-review-count]').textContent = `Showing ${Math.min(visible, filtered.length).toLocaleString()} of ${filtered.length.toLocaleString()} published reviews`;
      if (append) {
        const added = list.children[start];
        if (added) { added.tabIndex = -1; added.focus({ preventScroll: true }); }
      }
    }
    function applyFilters() {
      const query = normalize(search.value.trim());
      filtered = reviews.filter((review) =>
        (!category.value || review.category === category.value) &&
        (!rating.value || review.rating === Number(rating.value)) &&
        (!photos.checked || review.photos.length > 0) &&
        (!query || normalize(`${review.body} ${review.title} ${review.category} ${review.name}`).includes(query))
      );
      if (sort.value === 'lowest') filtered.sort((a, b) => a.rating - b.rating || b.date.localeCompare(a.date));
      if (sort.value === 'highest') filtered.sort((a, b) => b.rating - a.rating || b.date.localeCompare(a.date));
      visible = 12; render();
    }
    dialog.querySelector('[data-review-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      const rect = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) dialog.close();
    });
    dialog.addEventListener('close', () => { if (opener?.isConnected) opener.focus(); });
    async function load() {
      try {
        const data = await readLibrary(root.dataset.url);
        if (!root.isConnected) return;
        reviews = data.reviews;
        const gallery = root.querySelector('[data-review-gallery]');
        const photoReviews = reviews.filter((r) => r.photos.length);
        photoReviews.slice(0, 6).forEach((r) => gallery.append(photoButton(r, r.photos[0])));
        gallery.hidden = !photoReviews.length;
        root.querySelector('[data-photo-filter-label]').hidden = !photoReviews.length;
        root.querySelector('[data-review-snapshot]').textContent = `Review collection updated ${dateLabel(data.summary.snapshotDate)}.`;
        if (!compact) {
          controls.hidden = false;
          applyFilters();
          [category, rating, sort, photos].forEach((control) => control.addEventListener('change', applyFilters));
          let timer;
          search.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(applyFilters, 180); });
          more.addEventListener('click', () => { visible += 12; render(true); });
        }
      } catch (_) {
        root.querySelector('[data-review-error]').hidden = compact;
      }
    }
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) { observer.disconnect(); load(); }
      }, { rootMargin: '500px' });
      observer.observe(root);
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(root)) observer.disconnect();
      }, { once: true });
    } else load();
  }
  function initAll(scope) { scope.querySelectorAll('[data-review-library]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  else initAll(document);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
